import * as React from 'react'
import { Plus } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * AddPill — the dashed "+ Add …" affordance for adding to a collection (a tree
 * section, a tag cluster, a list). A low-emphasis pill (neutral gray, dashed
 * border, small) so it reads as a distinct *action* rather than one more
 * same-size line of content. Renders a `<button type="button">` by default; the
 * label is its accessible name via children.
 *
 *   <AddPill onClick={openCreate}>Add contact</AddPill>
 *
 * The leading Plus can be swapped via `icon` (a different glyph for a non-"add"
 * affordance) or omitted with `icon={null}`.
 */
export interface AddPillProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Leading icon. Defaults to a Plus; pass `null` to omit. */
  icon?: React.ReactNode
}

export const AddPill = React.forwardRef<HTMLButtonElement, AddPillProps>(
  ({ className, icon, children, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      data-slot="add-pill"
      // Neutral tone matches `neutralIconTone` (gray-500 → gray-600 text) so it
      // agrees with Button's `neutral` variant + CloseButton/HelpPopover; the
      // hover here darkens the dashed border instead of filling a bg, so it
      // can't reuse that constant wholesale.
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-dashed border-fbui-gray-300 px-2 py-0.5 text-xs font-medium text-fbui-gray-500 transition-colors hover:border-fbui-gray-400 hover:text-fbui-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand [&>svg]:size-3',
        className
      )}
      {...props}
    >
      {icon === undefined ? <Plus weight="bold" /> : icon}
      {children}
    </button>
  )
)
AddPill.displayName = 'AddPill'
